import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { NovaOrb } from "../components/NovaOrb";
import { BottomNav } from "../components/BottomNav";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const DIET_LABEL = { veg: "Vegetarian", nonveg: "Non-vegetarian" };
const EQUIPMENT_LABEL = {
  home_none: "No equipment",
  home_dumbbells: "Dumbbells at home",
  gym_machines: "Gym machines",
  gym_free_weights: "Full gym access",
};

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/onboarding/profile").then(({ data }) => setProfile(data)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const rows = profile?.onboarding_complete
    ? [
        { label: "Diet preference", value: DIET_LABEL[profile.diet_pref] || profile.diet_pref, testId: "profile-diet-pref" },
        { label: "Equipment", value: EQUIPMENT_LABEL[profile.equipment_tier] || profile.equipment_tier, testId: "profile-equipment" },
        { label: "Days per week", value: profile.days_per_week, testId: "profile-days-per-week" },
        { label: "Session length", value: `${profile.session_minutes} min`, testId: "profile-session-minutes" },
        { label: "Experience", value: (profile.experience || "").replace("_", " "), testId: "profile-experience" },
        { label: "Injuries", value: profile.injuries?.length ? profile.injuries.join(", ") : "None reported", testId: "profile-injuries" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-kaizen-bg text-kaizen-text pb-28" data-testid="profile-page">
      <div className="max-w-md mx-auto px-5 pt-8">
        <div className="flex flex-col items-center">
          <NovaOrb size={90} />
          <p className="font-display text-2xl mt-4" data-testid="profile-name">{user?.name}</p>
          <p className="text-sm text-kaizen-dim">{user?.email}</p>
        </div>

        {rows.length > 0 && (
          <div className="glass-card rounded-3xl p-6 mt-6" data-testid="profile-details-card">
            <p className="text-xs uppercase tracking-widest text-kaizen-dim mb-3">Your setup</p>
            <div className="space-y-2.5">
              {rows.map((r) => (
                <div key={r.label} className="flex items-center justify-between text-sm" data-testid={r.testId}>
                  <span className="text-kaizen-dim">{r.label}</span>
                  <span className="text-kaizen-text capitalize">{r.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="glass-card rounded-3xl p-6 mt-4">
          <p className="text-sm text-kaizen-dim leading-relaxed">
            Kaizen adapts to your life, not the other way around. Your plan, injuries and preferences
            stay saved across every device you log into.
          </p>
        </div>

        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full mt-6 py-3.5 rounded-full border border-white/15 text-sm text-kaizen-dim flex items-center justify-center gap-2 hover:border-kaizen-violetSoft/40 hover:text-kaizen-violetSoft transition-colors duration-300"
        >
          <LogOut size={15} />
          Log out
        </button>
      </div>
      <BottomNav />
    </div>
  );
}
